import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import Collapsible from '../Collapsible';

import CollapsibleIcon from '../icons/CollapsibleIcon';

import styles from './PaymentDetailsCard.module.scss';

import InfoTooltip from './InfoTooltip';

const defaultFooterLine = () => ({
  text: trans('payment.details.paid.to.landlord')
});

const resolveFooterLines = footer => {
  if (footer === undefined) {
    return [defaultFooterLine()];
  }
  if (footer == null) {
    return [];
  }
  if (typeof footer === 'string') {
    return footer ? [{ text: footer }] : [];
  }
  return footer;
};

const hasHeaderValue = value =>
  value != null && !(typeof value === 'string' && value === '');

const PaymentDetailsCard = ({
  title,
  headerValue = null,
  headerMeta = null,
  footer = undefined,
  isCollapsed = true,
  dataTest = null,
  children = null
}) => {
  const renderHeaderMeta = () =>
    headerMeta && headerMeta.text ? (
      <div className={styles['payment-details-card__header-meta']}>
        <span>{headerMeta.text}</span>
        {headerMeta.tooltipText ? (
          <InfoTooltip
            title={headerMeta.text}
            className={styles['payment-details-card__tooltip']}
            tooltipText={headerMeta.tooltipText}
          />
        ) : null}
      </div>
    ) : null;

  const renderHeaderValue = () => (
    <span className={styles['payment-details-card__header-value-amount']}>
      {typeof headerValue === 'function' ? headerValue() : headerValue}
    </span>
  );

  const renderHeaderBlock = () => (
    <div className={styles['payment-details-card__header-text']}>
      <span className={styles['payment-details-card__title']}>{title}</span>
      {hasHeaderValue(headerValue) ? (
        <div className={styles['payment-details-card__header-value-wrap']}>
          {renderHeaderValue()}
        </div>
      ) : null}
      {renderHeaderMeta()}
    </div>
  );

  const footerLines = resolveFooterLines(footer);

  const renderFooter = () => {
    if (footerLines.length === 1 && !footerLines[0].tooltipText) {
      return (
        <span className={styles['payment-details-card__footer']}>
          {footerLines[0].text}
        </span>
      );
    }

    if (footerLines.length === 0) {
      return null;
    }
    return (
      <div className={styles['payment-details-card__footer-stack']}>
        {footerLines.map((line, index) => (
          <div
            key={index}
            className={styles['payment-details-card__footer-line']}
          >
            <span>{line.text}</span>
            {line.tooltipText ? (
              <InfoTooltip
                title={line.text}
                className={styles['payment-details-card__tooltip']}
                tooltipText={line.tooltipText}
              />
            ) : null}
          </div>
        ))}
      </div>
    );
  };

  const renderBodyInner = () => (
    <>
      {typeof children === 'function' ? children() : children}
      {renderFooter()}
    </>
  );

  return (
    <div className={styles['payment-details-card']} data-test={dataTest}>
      <Collapsible
        bodyClassName={styles['payment-details-card__collapsible-body']}
        dataTest={dataTest}
        hasIcon
        headerClassName={styles['payment-details-card__collapsible-header']}
        iconClosed={<CollapsibleIcon collapsed />}
        iconOpened={<CollapsibleIcon collapsed={false} />}
        isCollapsed={isCollapsed}
        renderBody={renderBodyInner}
        renderHeader={renderHeaderBlock}
      />
    </div>
  );
};

const HeaderMetaShape = PropTypes.shape({
  text: PropTypes.string.isRequired,
  tooltipText: PropTypes.string
});

const FooterLineShape = PropTypes.shape({
  text: PropTypes.string.isRequired,
  tooltipText: PropTypes.string
});

PaymentDetailsCard.propTypes = {
  title: PropTypes.string.isRequired,
  headerValue: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
    PropTypes.string,
    PropTypes.number
  ]),
  headerMeta: HeaderMetaShape,
  footer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null]),
    PropTypes.arrayOf(FooterLineShape)
  ]),
  isCollapsed: PropTypes.bool,
  dataTest: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node])
};

export default PaymentDetailsCard;
